// Google Analytics 4 adapter for the agent dashboard (owner order 2026-09-14).
// One GA4 property for the whole installation: every company and every agent
// is measured by ids and roles only. Nothing that identifies a customer or a
// message ever leaves: paths are normalised (record ids → ":id"), event
// properties are whitelisted to plain ids/strings, and the user is identified
// by number, never by email or name.
const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js?id=';
const ALLOWED_PROP = /^[a-z0-9_]{1,40}$/;
const ACCOUNT_SEGMENT = /\/accounts\/(\d+)/;

export const normalisePath = path =>
  String(path || '')
    .replace(/\?.*$/, '')
    .replace(ACCOUNT_SEGMENT, '/accounts/:account')
    .replace(/\/\d+(?=\/|$)/g, '/:id');

export const eventName = name =>
  String(name || 'event')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'event';

const scalar = value =>
  typeof value === 'number' || typeof value === 'boolean'
    ? value
    : String(value).slice(0, 100);

export class Ga4Adapter {
  constructor(measurementId) {
    this.measurementId = measurementId;
    this.context = {};
    this.ready = false;
  }

  init() {
    if (this.ready || !this.measurementId || typeof document === 'undefined')
      return;
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
      };
    window.gtag('js', new Date());
    // page_view is sent by the router hook with a normalised path.
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `${GTAG_SRC}${encodeURIComponent(this.measurementId)}`;
    document.head.appendChild(script);
    this.ready = true;
  }

  identify(user) {
    if (!this.ready || !user?.id) return;
    const { accounts = [], account_id: accountId } = user;
    const account = accounts.find(item => item.id === accountId);
    this.context = {
      account_id: accountId ? String(accountId) : undefined,
      account_name: account?.name,
      role: account?.role || user.role,
      locale: user.ui_settings?.locale || document.documentElement.lang,
    };
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      user_id: `user-${user.id}`,
    });
    window.gtag('set', 'user_properties', this.context);
  }

  track(name, properties = {}) {
    if (!this.ready) return;
    const params = { ...this.context };
    Object.entries(properties || {}).forEach(([key, value]) => {
      if (!ALLOWED_PROP.test(key) || value === undefined || value === null)
        return;
      if (typeof value === 'object') return;
      params[key] = scalar(value);
    });
    window.gtag('event', eventName(name), params);
  }

  page(pageName, { path } = {}) {
    if (!this.ready) return;
    window.gtag('event', 'page_view', {
      ...this.context,
      page_title: pageName || 'dashboard',
      page_path: normalisePath(path),
      page_location: `${window.location.origin}${normalisePath(path)}`,
    });
  }
}

export default Ga4Adapter;
